import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { authApi, getApiErrorMessage, ledgerMirrorApi } from 'api';
import { useAuth } from 'contexts/AuthContext';
import { useAccountingView } from 'hooks/useAccountingView';

const SECRET_CLICKS = 5;
const SECRET_WINDOW_MS = 2500;

export function useLedgerSecretToggle() {
  const toast = useToast();
  const { user, setUser } = useAuth();
  const { canToggleAccountingView, accountingViewMode } = useAccountingView();
  const canToggleLedger = !!(
    (user?.isDirector || user?.role === 'director' || user?.isAdmin || user?.role === 'admin') &&
    user?.ledgerMirrorEnabled
  );
  const canToggle = canToggleLedger || canToggleAccountingView;
  const activeLedger = user?.activeLedger || 'primary';
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  const loadStatus = useCallback(async () => {
    if (!canToggleLedger) return;
    try {
      const data = await ledgerMirrorApi.status();
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, [canToggleLedger]);

  useEffect(() => {
    if (modalOpen) {
      loadStatus();
    }
  }, [modalOpen, loadStatus]);

  const handleSecretClick = useCallback(() => {
    if (!canToggle) return;

    clickCountRef.current += 1;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }
    clickTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, SECRET_WINDOW_MS);

    if (clickCountRef.current >= SECRET_CLICKS) {
      clickCountRef.current = 0;
      setModalOpen(true);
    }
  }, [canToggle]);

  const applyLedger = useCallback(
    async (ledger) => {
      setSaving(true);
      try {
        const updated = await authApi.updateActiveLedger(ledger);
        if (setUser) {
          setUser(updated);
        }
        setModalOpen(false);
        window.location.reload();
      } catch (e) {
        toast({
          title: 'Не удалось переключить версию БД',
          description: getApiErrorMessage(e),
          status: 'error',
        });
      } finally {
        setSaving(false);
      }
    },
    [setUser, toast]
  );

  const applyAccounting = useCallback(
    async (mode) => {
      setSaving(true);
      try {
        const updated = await authApi.updateAccountingViewMode(mode);
        if (setUser) {
          setUser(updated);
        }
        setModalOpen(false);
        window.location.reload();
      } catch (e) {
        toast({
          title: 'Не удалось переключить режим учёта',
          description: getApiErrorMessage(e),
          status: 'error',
        });
      } finally {
        setSaving(false);
      }
    },
    [setUser, toast]
  );

  const modalBg = useColorModeValue('white', 'navy.800');
  const muted = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const counts = status?.counts || {};

  const modal = (
    <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} isCentered size="lg">
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>Версия учёта</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {canToggleAccountingView ? (
            <>
              <Text fontSize="sm" color={muted} mb={2}>
                Просмотр: амбулатория, доп. услуги и перевязки — как есть.
                Койко-дни только онлайн; нал за палату не входит.
              </Text>
              <VStack align="stretch" spacing={2} mb={4}>
                <Button
                  variant={accountingViewMode === 'internal' ? 'solid' : 'outline'}
                  colorScheme="brand"
                  onClick={() => applyAccounting('internal')}
                  isLoading={saving}
                >
                  Внутренний учёт — все данные
                </Button>
                <Button
                  variant={accountingViewMode === 'tax' ? 'solid' : 'outline'}
                  colorScheme="orange"
                  onClick={() => applyAccounting('tax')}
                  isLoading={saving}
                >
                  Просмотр
                </Button>
              </VStack>
            </>
          ) : null}
          {canToggleLedger ? (
            <>
          <Text fontSize="sm" color={muted} mb={4}>
            Переключение общее для всей клиники: регистратура, касса и врачи переходят
            вместе. V1 — оригинал. V2 — зеркало: амбулатория, мини-операции, доп. услуги
            и перевязки сразу в обе стороны; стационар — по 3 пациента в сутки.
          </Text>
          <VStack align="stretch" spacing={2} mb={4}>
            <Button
              variant={activeLedger === 'primary' ? 'solid' : 'outline'}
              colorScheme="teal"
              onClick={() => applyLedger('primary')}
              isLoading={saving}
            >
              V1 · Оригинал
            </Button>
            <Button
              variant={activeLedger === 'mirror' ? 'solid' : 'outline'}
              colorScheme="purple"
              onClick={() => applyLedger('mirror')}
              isLoading={saving}
            >
              V2 · Зеркало
            </Button>
          </VStack>
          {status ? (
            <Box>
              <Text fontSize="sm" fontWeight="600" mb={2}>
                Очередь зеркала
              </Text>
              <SimpleGrid columns={4} spacing={2} mb={3}>
                <StatusChip label="pending" value={counts.pending ?? 0} />
                <StatusChip label="synced" value={counts.synced ?? 0} />
                <StatusChip label="ops today" value={status.operationsSyncedToday ?? 0} />
                <StatusChip label="stat today" value={status.inpatientSyncedToday ?? 0} />
              </SimpleGrid>
              <Text fontSize="xs" color={muted}>
                Health: {status.health?.message || '—'} · ops/day {status.operationsPerDay} ·
                inpatient/day {status.inpatientPerDay}
              </Text>
            </Box>
          ) : null}
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>
            Закрыть
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  return { handleSecretClick, modal, canToggle, activeLedger };
}

function StatusChip({ label, value }) {
  return (
    <Box borderWidth="1px" borderRadius="md" px={2} py={1} textAlign="center">
      <Text fontSize="xs" opacity={0.7}>
        {label}
      </Text>
      <Text fontWeight="700">{value}</Text>
    </Box>
  );
}

/** Секретный триггер: 5 кликов по заголовку в хедере у директора. */
export function LedgerVersionSecretBrand({ children }) {
  const { canToggle, handleSecretClick, modal } = useLedgerSecretToggle();

  return (
    <Box
      as="span"
      onClick={canToggle ? handleSecretClick : undefined}
      cursor={canToggle ? 'default' : undefined}
      userSelect={canToggle ? 'none' : undefined}
    >
      {children}
      {modal}
    </Box>
  );
}
